import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { filter, Observable } from 'rxjs';
import { CouponService } from '@api/services/coupon.service';
import { ProgramList } from '@api/models/BoLanka/Contract/Coupon/program-list';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { tap } from 'rxjs/operators';
import { DiscProgramFinishRequest } from '@api/models/BoLanka/Contract/Coupon/disc-program-finish-request';
import { DiscProgramActivateRequest } from '@api/models/BoLanka/Contract/Coupon/disc-program-activate-request';
import { DiscProgramAddRequest } from '@api/models/BoLanka/Contract/Coupon/disc-program-add-request';
import { ProgramAdd } from '@api/models/BoLanka/Contract/Coupon/program-add';
import { DiscProgramSetRequest } from '@api/models/BoLanka/Contract/Coupon/disc-program-set-request';
import { LocalStorage } from '@core/storage/local-storage';
import { StorageKeys } from '@core/storage/storage.keys';
import { ProgramGet } from '@api/models/BoLanka/Contract/Coupon/program-get';
import { DiscTariffListResponse } from '@api/models/BoLanka/Contract/Coupon/disc-tariff-list-response';
import { DiscountProgramType } from '../models/discounts-program-type';
import { CouponStatusList } from '@api/models/BoLanka/Contract/Coupon/coupon-status-list';
import { CustomRxState } from '@core/state/rx.state';
import { CouponList } from '@api/models/BoLanka/Contract/Coupon/coupon-list';
import { CouponFilterBody } from '../models/coupon-filter.model';


interface IDiscountProgramState {
    loading: Record<StartWithUnit, boolean>;
    discountPrograms: ProgramList[];
    discountProgram: ProgramGet;
    discountProgramTariffs: DiscTariffListResponse[];
    prgType: number;
    couponStatusList: CouponStatusList[];
    couponList: CouponList[];
}

interface DiscountProgramActions {
    requestDiscountPrograms: ProgramList[];
    requestDiscountProgramTariffs: DiscTariffListResponse[];
    requestCouponList: CouponList[];
    setDiscountProgram: ProgramGet;
    setPrgType: number;
}

@Injectable()
export class DiscountProgramState extends CustomRxState<IDiscountProgramState> {
    @LocalStorage(StorageKeys.Access)
    protected token: string;

    readonly #actions: RxActions<DiscountProgramActions> = new RxActionFactory<DiscountProgramActions>().create();
    readonly #couponService: CouponService = inject(CouponService);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly discountPrograms$: Observable<ProgramList[]> = this.select('discountPrograms');
    public readonly discountProgramTariffs$: Observable<DiscTariffListResponse[]> = this.select('discountProgramTariffs');
    public readonly couponList$: Observable<CouponList[]> = this.select('couponList');
    public readonly couponStatusList$: Observable<CouponStatusList[]> = this.selectLazy('couponStatusList', this.requestCouponStatusList);

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestDiscountPrograms();
    }

    public requestDiscountPrograms(): void {
        this.hold(
            this.#couponService.apiCouponDiscProgramListGet().pipe(
                startWithLoading(this, StartWithUnit.RequestDiscountPrograms),
            ),
            this.#actions.requestDiscountPrograms,
        );
    }

    public requestDiscountProgramTariffs(prgType: number): void {
        this.hold(
            this.#couponService.apiCouponDiscTariffListGet({ prgType }),
            this.#actions.requestDiscountProgramTariffs,
        );
    }

    public setPrgType(prgType: number): void {
        this.#actions.setPrgType(prgType);
    }

    public requestCouponList(body: CouponFilterBody): void {
        this.hold(
            this.#couponService.apiCouponDiscCouponListGet(body).pipe(
                startWithLoading(this, StartWithUnit.CouponSearch),
            ),
            this.#actions.requestCouponList,
        );
    }

    public addDiscountProgram(body: DiscProgramAddRequest): Observable<ProgramAdd> {
        return this.#couponService.apiCouponDiscProgramAddPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDiscountProgram),
            tap(() => this.requestDiscountPrograms()),
        );
    }

    public editDiscountProgram(body: DiscProgramSetRequest): Observable<void> {
        return this.#couponService.apiCouponDiscProgramSetPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditDiscountProgram),
            tap(() => this.requestDiscountPrograms()),
        );
    }

    public activateDiscountProgram(body: DiscProgramActivateRequest): Observable<void> {
        return this.#couponService.apiCouponDiscProgramActivatePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.ChangeStatusDiscountProgram),
            tap(() => this.requestDiscountPrograms()),
        );
    }

    public finishDiscountProgram(body: DiscProgramFinishRequest): Observable<void> {
        return this.#couponService.apiCouponDiscProgramFinishPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.ChangeStatusDiscountProgram),
            tap(() => this.requestDiscountPrograms()),
        );
    }

    public deleteDiscountProgram(prgId: number): Observable<void> {
        return this.#couponService.apiCouponDiscProgramDelPrgIdDelete({ prgId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteDiscountProgram),
            tap(() => this.requestDiscountPrograms()),
        );
    }

    public exportCoupons(prgId: number): Observable<Blob> {
        return this.#couponService.apiCouponDiscCouponListToCsvGet({ prgId, token: this.token });
    }

    public getDetailDataDiscountProgram(prgId: number): Observable<ProgramGet> {
        return this.#couponService.apiCouponDiscProgramGetGet({ prgId }).pipe(
            tap((program: ProgramGet) => this.#actions.setDiscountProgram(program)),
        );
    }

    private requestCouponStatusList(): Observable<CouponStatusList[]> {
        return this.#couponService.apiCouponDiscCouponStatusListGet();
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            prgType: null,
            discountProgramTariffs: null,
            discountPrograms: null,
            couponList: null,
        });
    }

    private connectSelectors(): void {
        this.connect('discountPrograms', this.#actions.requestDiscountPrograms$);
        this.connect('discountProgramTariffs', this.#actions.requestDiscountProgramTariffs$);
        this.connect('couponList', this.#actions.requestCouponList$);
        this.connect('discountProgram', this.#actions.setDiscountProgram$.pipe(
            filter(Boolean),
            tap(() => this.set({ couponList: null })),
        ));

        this.connect('prgType', this.#actions.setPrgType$.pipe(
            tap((prgType: number) => {
                if ([DiscountProgramType.TicketVouchers, DiscountProgramType.PromoTickets].includes(prgType)) {
                    this.requestDiscountProgramTariffs(prgType);
                } else {
                    this.set({ discountProgramTariffs: null });
                }
            }),
        ));
    }
}
